import { Box, styled, Typography } from "@material-ui/core";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { Skeleton } from "@material-ui/lab";
import type { CSSProperties } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { CredStatus } from "../_lib/credentialing-config";
import { CREDENTIALING_THRESHOLDS } from "../_lib/credentialing-config";
import { EXAM_COLORS } from "../_lib/epocus-exam-colors";
import {
  EPOCUS_BG,
  EPOCUS_BORDER,
  EPOCUS_INACTIVE,
  EPOCUS_SELECTED_BG,
  EPOCUS_STATUS_COMPLETE_BG,
  EPOCUS_STATUS_COMPLETE_TEXT,
  EPOCUS_STATUS_DRAFT_BG,
  EPOCUS_STATUS_DRAFT_TEXT,
  EPOCUS_STATUS_REVIEWED_BG,
  EPOCUS_STATUS_REVIEWED_TEXT,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../_lib/epocus-palette";
import { epocusTheme } from "../_lib/epocus-theme-provider";
import { usePerformerName } from "../_lib/hooks/use-performer-name";
import { getScoreColor } from "../_lib/qa-score-colors";
import {
  BigValue,
  Card,
  MetaLabel,
  PageRoot,
  SectionTitle,
} from "../_lib/stat-primitives";
import { linkToEpocusPerformer } from "../links";
import type { PerformerRow } from "./_lib/use-program-stats";
import { useProgramStats } from "./_lib/use-program-stats";

// ─── Page Header ──────────────────────────────────────────────────────────────

const PageHeader: FC = () => (
  <Box>
    <Typography
      style={{
        fontSize: rem(22),
        fontWeight: 700,
        color: EPOCUS_TEXT_PRIMARY,
        lineHeight: 1.2,
      }}
    >
      Program Dashboard
    </Typography>
    <Typography
      style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY, marginTop: 4 }}
    >
      System-wide POCUS activity and credentialing overview
    </Typography>
  </Box>
);

// ─── Summary Stats ────────────────────────────────────────────────────────────

const StatsGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 12,
  [epocusTheme.breakpoints.up("sm")]: {
    gridTemplateColumns: "1fr 1fr 1fr 1fr",
  },
});

const StatCard: FC<{ label: string; value: string | number }> = ({
  label,
  value,
}) => (
  <Card>
    <MetaLabel>{label}</MetaLabel>
    <BigValue style={{ fontSize: rem(28) }}>{value}</BigValue>
  </Card>
);

// ─── Inline Progress Bar ──────────────────────────────────────────────────────

const ColoredProgBar: FC<{ value: number; max: number; color: string }> = ({
  value,
  max,
  color,
}) => {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <Box style={{ minWidth: 64 }}>
      <Box
        style={{
          position: "relative",
          height: 6,
          borderRadius: 3,
          backgroundColor: EPOCUS_BG,
          overflow: "hidden",
          marginBottom: 3,
        }}
      >
        <Box
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            height: "100%",
            width: `${pct}%`,
            borderRadius: 3,
            backgroundColor: color,
          }}
        />
      </Box>
      <Typography style={{ fontSize: rem(10), color: EPOCUS_TEXT_SECONDARY }}>
        {value} / {max}
      </Typography>
    </Box>
  );
};

// ─── Status Badge ─────────────────────────────────────────────────────────────

const STATUS_BADGE_STYLES: Record<
  CredStatus,
  { bg: string; text: string; label: string }
> = {
  credentialed: {
    bg: EPOCUS_STATUS_REVIEWED_BG,
    text: EPOCUS_STATUS_REVIEWED_TEXT,
    label: "✓ Credentialed",
  },
  in_progress: {
    bg: EPOCUS_STATUS_DRAFT_BG,
    text: EPOCUS_STATUS_DRAFT_TEXT,
    label: "In Progress",
  },
  not_started: {
    bg: EPOCUS_STATUS_COMPLETE_BG,
    text: EPOCUS_STATUS_COMPLETE_TEXT,
    label: "Not Started",
  },
};

const StatusBadge: FC<{ credStatus: CredStatus }> = ({ credStatus }) => {
  const { bg, text, label } = STATUS_BADGE_STYLES[credStatus];
  return (
    <Box
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 8px",
        borderRadius: 20,
        fontSize: rem(11),
        fontWeight: 700,
        whiteSpace: "nowrap" as const,
        backgroundColor: bg,
        color: text,
      }}
    >
      {label}
    </Box>
  );
};

// ─── Performer Table ──────────────────────────────────────────────────────────

const TABLE_CELL_STYLE: CSSProperties = {
  padding: "10px 12px",
  textAlign: "left",
  verticalAlign: "middle",
  borderBottom: `1px solid ${EPOCUS_BORDER}`,
  whiteSpace: "nowrap" as const,
};

const TABLE_HEAD_CELL_STYLE: CSSProperties = {
  ...TABLE_CELL_STYLE,
  fontSize: rem(11),
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase" as const,
  color: EPOCUS_TEXT_SECONDARY,
  backgroundColor: EPOCUS_BG,
};

const CRED_REQ = {
  efast: CREDENTIALING_THRESHOLDS.efast.totalExamsMin,
  cardiac: CREDENTIALING_THRESHOLDS.cardiac.totalExamsMin,
  lung: CREDENTIALING_THRESHOLDS.lung.totalExamsMin,
} as const;

const ClickableRow = styled("tr")({
  cursor: "pointer",
  "&:hover": {
    backgroundColor: EPOCUS_SELECTED_BG,
  },
});

const PerformerTableRow: FC<{ row: PerformerRow }> = ({ row }) => {
  const { displayName, loading } = usePerformerName(row.performerId);
  const navigate = useNavigate();
  const to = linkToEpocusPerformer(row.performerId);

  return (
    <ClickableRow onClick={() => navigate(to)}>
      <td style={TABLE_CELL_STYLE}>
        <Link
          to={to}
          onClick={(event) => event.stopPropagation()}
          style={{
            fontSize: rem(13),
            fontWeight: 600,
            color: EPOCUS_TEXT_PRIMARY,
            textDecoration: "none",
          }}
        >
          {loading ? <Skeleton variant="text" width={140} /> : displayName}
        </Link>
      </td>
      <td style={TABLE_CELL_STYLE}>
        <Typography style={{ fontSize: rem(13), color: EPOCUS_TEXT_PRIMARY }}>
          {row.totalExams}
        </Typography>
      </td>
      <td style={TABLE_CELL_STYLE}>
        <Typography
          style={{
            fontSize: rem(13),
            fontWeight: 700,
            color: getScoreColor(row.avgScore),
          }}
        >
          {row.avgScore}
        </Typography>
      </td>
      <td style={TABLE_CELL_STYLE}>
        <ColoredProgBar
          value={row.efastCount}
          max={CRED_REQ.efast}
          color={EXAM_COLORS.efast}
        />
      </td>
      <td style={TABLE_CELL_STYLE}>
        <ColoredProgBar
          value={row.cardiacCount}
          max={CRED_REQ.cardiac}
          color={EXAM_COLORS.cardiac}
        />
      </td>
      <td style={TABLE_CELL_STYLE}>
        <ColoredProgBar
          value={row.lungCount}
          max={CRED_REQ.lung}
          color={EXAM_COLORS.lung}
        />
      </td>
      <td style={TABLE_CELL_STYLE}>
        <StatusBadge credStatus={row.credStatus} />
      </td>
      <td style={{ ...TABLE_CELL_STYLE, textAlign: "right" }}>
        <ChevronRightIcon
          style={{ fontSize: rem(18), color: EPOCUS_INACTIVE }}
          aria-hidden
        />
      </td>
    </ClickableRow>
  );
};

const PerformerOverview: FC<{ rows: PerformerRow[] }> = ({ rows }) => (
  <Box>
    <SectionTitle>Performer Overview</SectionTitle>
    <Card style={{ padding: 0, overflowX: "auto" }}>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          minWidth: 600,
        }}
      >
        <thead>
          <tr>
            {(
              [
                "Performer",
                "Exams",
                "Avg Score",
                `eFAST (${CRED_REQ.efast})`,
                `Cardiac (${CRED_REQ.cardiac})`,
                `Lung (${CRED_REQ.lung})`,
                "Status",
                "",
              ] as const
            ).map((col, index) => (
              <th
                key={col === "" ? `spacer-${index}` : col}
                style={TABLE_HEAD_CELL_STYLE}
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={8}
                style={{
                  ...TABLE_CELL_STYLE,
                  textAlign: "center",
                  color: EPOCUS_TEXT_SECONDARY,
                  fontSize: rem(13),
                  padding: "24px 12px",
                }}
              >
                No performer data yet — seed some encounters to get started.
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <PerformerTableRow key={row.performerId} row={row} />
            ))
          )}
        </tbody>
      </table>
    </Card>
    <Typography
      style={{
        fontSize: rem(11),
        color: EPOCUS_TEXT_SECONDARY,
        marginTop: 8,
        paddingLeft: 2,
      }}
    >
      Credentialed when all program types are met · eFAST {CRED_REQ.efast} ·
      Cardiac {CRED_REQ.cardiac} · Lung {CRED_REQ.lung} · min avg score{" "}
      {CREDENTIALING_THRESHOLDS.efast.avgScoreMin.toFixed(1)} · select a
      performer for their full record
    </Typography>
  </Box>
);

// ─── Page ─────────────────────────────────────────────────────────────────────

export const EpocusProgramPage: FC = () => {
  const {
    activePerformers,
    examsThisMonth,
    avgQaScore,
    credentialedFraction,
    performerRows,
  } = useProgramStats();

  return (
    <PageRoot>
      <PageHeader />

      <StatsGrid>
        <StatCard label="Active Performers" value={activePerformers} />
        <StatCard label="Exams This Month" value={examsThisMonth} />
        <StatCard label="Avg QA Score" value={avgQaScore} />
        <StatCard label="Credentialed" value={credentialedFraction} />
      </StatsGrid>

      <PerformerOverview rows={performerRows} />
    </PageRoot>
  );
};
